/**
 * BuddyGroupEditPage — admin/delegate edit view for a buddy group.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useBuddyGroup } from '../api';
import { BuddyGroupForm } from '../components/BuddyGroupForm';

export default function BuddyGroupEditPage() {
  const { rodeoId, groupId } = useParams<{ rodeoId: string; groupId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useBuddyGroup(groupId!);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to List
        </Button>
        <div className="text-sm text-destructive">Error loading buddy group.</div>
      </div>
    );
  }

  const { group } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups/${groupId}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Edit: {group.group_name || '(unnamed group)'}</h1>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Edit Group</CardTitle>
        </CardHeader>
        <CardContent>
          <BuddyGroupForm
            group={group}
            onSaved={() => navigate(`/rodeos/${rodeoId}/buddy-groups/${groupId}`)}
            onCancel={() => navigate(`/rodeos/${rodeoId}/buddy-groups/${groupId}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
